import { z } from "zod";

import { CompanySchema } from "@/pages/Public/pages/Login/schema/login";

import { ERROR_TYPE } from "@/utils";

import { clearMarkdownAndHTMLAndTrim } from "@/utils/clearMarkdownAndHTMLAndTrim";

import { ToDoTagInfoSchema } from "@/schemas/tag.schema";

import { IndustrySchema } from "../../../ManageIndustries/industry/schema/industry";
import { ShowingMode } from "../enum/ShowingMode.enum";
import { ToDoPriority } from "../enum/ToDoPriority.enum";
import { ToDoStatus } from "../enum/ToDoStatus.enum";
import { ToDoType } from "../enum/ToDoType.enum";

// Re-export from shared schema for backward compatibility
export { ToDoTagInfoSchema, ToDoTagResponseSchema, type ToDoTagInfo } from "@/schemas/tag.schema";

const { REQUIRED } = ERROR_TYPE;

export const ToDoSubTaskInfoSchema = z.object({
	id: z.string().optional(),
	title: z.string().min(1, { message: REQUIRED }),
	description: z.string().optional(),
	question: z.string().optional(),
	questionDescription: z.string().optional(),
});

export const ToDoInfoSchema = z.object({
	id: z.number().optional(),
	title: z.string().optional(),
	description: z.string().optional(),
	requirement: z.string().optional(),
	recommendation: z.string().optional(),
	question: z.string().optional(),
	type: z.nativeEnum(ToDoType).optional(),
	tagId: z.number().optional(),
	tag: ToDoTagInfoSchema.optional(),
	industryIds: z.array(z.number()).optional().nullable(),
	companySizeIds: z.array(z.number()).optional().nullable(),
	companyRevenueIds: z.array(z.number()).optional().nullable(),
	companyBalanceIds: z.array(z.number()).optional().nullable(),
	showingMode: z.nativeEnum(ShowingMode).optional(),
	order: z.number().optional(),
	isOutOfDate: z.boolean().optional(),
	priority: z.nativeEnum(ToDoPriority).optional(),
	subTasks: z.array(ToDoSubTaskInfoSchema).optional(),
	industries: z.array(IndustrySchema).optional(),
});

export const CreateToDoInfoSchema = z.object({
	title: z.string().min(1, { message: REQUIRED }).default(""),
	description: z
		.string()
		.min(1, { message: REQUIRED })
		.superRefine((val, ctx) => {
			if (clearMarkdownAndHTMLAndTrim(val).trim() === "") {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: REQUIRED,
				});
			}
		}),
	recommendation: z.string().optional(),
	requirement: z.string().optional(),

	question: z.string().optional(),
	type: z
		.nativeEnum(ToDoType)
		.nullable()
		.refine((val) => val !== null, { message: REQUIRED }),
	tag: ToDoTagInfoSchema.required(),
	industryIds: z.array(z.number()).optional().nullable(),
	companySizeIds: z.array(z.number()).optional().nullable(),
	companyRevenueIds: z.array(z.number()).optional().nullable(),
	showingMode: z
		.nativeEnum(ShowingMode)
		.nullable()
		.refine((val) => val !== null, { message: REQUIRED }),
	priority: z.nativeEnum(ToDoPriority).optional(),
	subTasks: z.array(ToDoSubTaskInfoSchema).optional(),
});

export const UpdateToDoInfoSchema = z.object({
	id: z.number(),
	title: z.string().min(1, { message: REQUIRED }).optional(),
	description: z
		.string()
		.min(1, { message: REQUIRED })
		.superRefine((val, ctx) => {
			if (clearMarkdownAndHTMLAndTrim(val).trim() === "") {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: REQUIRED,
				});
			}
		})
		.optional(),
	recommendation: z.string().optional().nullable(),
	question: z.string().optional(),
	type: z.nativeEnum(ToDoType).optional(),
	tag: ToDoTagInfoSchema.optional(),
	industryIds: z.array(z.number()).optional().nullable(),
	companySizeIds: z.array(z.number()).optional().nullable(),
	companyRevenueIds: z.array(z.number()).optional().nullable(),
	companyBalanceIds: z.array(z.number()).optional().nullable(),
	requirement: z.string().optional(),
	isOutOfDate: z.union([z.boolean(), z.nativeEnum(ShowingMode)]).optional(),
	priority: z.nativeEnum(ToDoPriority).optional(),
	subTasks: z.array(ToDoSubTaskInfoSchema).optional(),
});

export const CompanyToDoProgressInfoDtoSchema = z.object({
	id: z.number().optional(),
	companyId: z.number().optional(),
	company: CompanySchema.optional(),
	toDoId: z.number().optional(),
	toDo: ToDoInfoSchema.optional(),
	status: z.nativeEnum(ToDoStatus).optional(),
	subTasks: z.record(z.string(), z.nativeEnum(ToDoStatus)).optional(),
	priority: z.nativeEnum(ToDoPriority).optional(),
	createdAt: z.number(),
	updatedAt: z.number(),
});

export const DeleteToDoResponseSchema = z.object({
	id: z.number(),
});

export const CompanyToDoProgressInfoResponseSchema = z.array(CompanyToDoProgressInfoDtoSchema);
export const ToDoResponseSchema = z.array(ToDoInfoSchema);

export type ToDoInfo = z.infer<typeof ToDoInfoSchema>;
export type CompanyToDoProgressInfoDto = z.infer<typeof CompanyToDoProgressInfoDtoSchema>;
export type CompanyToDoProgressInfoResponse = z.infer<typeof CompanyToDoProgressInfoResponseSchema>;
export type CreateToDoInfo = z.infer<typeof CreateToDoInfoSchema>;
export type UpdateToDoInfo = z.infer<typeof UpdateToDoInfoSchema>;
export type DeleteToDoResponse = z.infer<typeof DeleteToDoResponseSchema>;
export type ToDoSubTaskInfo = z.infer<typeof ToDoSubTaskInfoSchema>;
